/**
 * Student Discount page — isolated JS
 * - Promo banner carousel (.sd-promo)
 * - Modal open/close for [data-sd-open-modal]
 * - Gorgias bridge: also answers STUDENT_LOCALE_REQUEST from the Gorgias
 *   iframe (docs/gorgias-student-extra-html.html) with this storefront
 *   page's locale, since the iframe is cross-origin and cannot reliably
 *   read it via document.referrer.
 * FAQ + product slider keep using theme scripts.
 */
(() => {
  /* ---------- Promo carousel ---------- */
  const initPromo = (root) => {
    if (!root || root.dataset.sdReady === 'true') return;

    const track = root.querySelector('[data-sd-track]');
    const slides = root.querySelectorAll('[data-sd-slide]');
    const prev = root.querySelector('[data-sd-prev]');
    const next = root.querySelector('[data-sd-next]');
    const dotsWrap = root.querySelector('[data-sd-dots]');

    if (!track || slides.length === 0) return;

    let index = 0;
    const total = slides.length;

    const renderDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (let i = 0; i < total; i += 1) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `sd-promo__dot${i === index ? ' is-active' : ''}`;
        btn.setAttribute('aria-label', `Slide ${i + 1}`);
        btn.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(btn);
      }
    };

    const goTo = (nextIndex) => {
      index = (nextIndex + total) % total;
      track.style.transform = `translateX(-${index * 100}%)`;
      if (dotsWrap) {
        dotsWrap.querySelectorAll('.sd-promo__dot').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      }
    };

    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));

    let startX = 0;
    track.addEventListener(
      'touchstart',
      (e) => {
        startX = e.changedTouches[0].clientX;
      },
      { passive: true }
    );
    track.addEventListener(
      'touchend',
      (e) => {
        const delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) < 40) return;
        goTo(delta < 0 ? index + 1 : index - 1);
      },
      { passive: true }
    );

    renderDots();
    goTo(0);
    root.dataset.sdReady = 'true';
  };

  const bootPromo = () => {
    document.querySelectorAll('[data-sd-promo]').forEach(initPromo);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPromo);
  } else {
    bootPromo();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const promo = event.target?.querySelector?.('[data-sd-promo]') || event.target?.closest?.('[data-sd-promo]');
    if (promo) {
      promo.dataset.sdReady = 'false';
      initPromo(promo);
    }
  });

  /* ---------- Modal (Gorgias bridge + lifecycle) ---------- */
  const SELECTOR_MODAL = '[data-sd-modal]';
  const SELECTOR_OPEN = '[data-sd-open-modal]';
  const GORGIAS_ORIGIN = 'https://contact.gorgias.help';
  const GORGIAS_MESSAGE_SOURCE = 'ultimate-setup-student-gorgias';
  const FORM_RESET_ATTR = 'sdNeedsFormReset';
  const ORIGINAL_SRC_ATTR = 'sdOriginalSrc';
  /* Must match the locale keys defined in the Gorgias Student Extra HTML's COPY object. */
  const SUPPORTED_LOCALES = ['de', 'en', 'fr', 'it', 'es', 'ar'];

  let lastModalOpener = null;
  let gorgiasBridgeReady = false;

  const getModal = () => document.querySelector(SELECTOR_MODAL);

  const isFocusable = (element) => {
    if (!element || typeof element.matches !== 'function') return false;
    return element.matches(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  };

  const releaseModalFocus = (modal) => {
    const active = document.activeElement;
    if (modal && active && modal.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }

    if (lastModalOpener && document.contains(lastModalOpener) && isFocusable(lastModalOpener)) {
      lastModalOpener.focus({ preventScroll: true });
      return;
    }

    if (document.body && typeof document.body.focus === 'function') {
      document.body.setAttribute('tabindex', '-1');
      document.body.focus({ preventScroll: true });
      document.body.removeAttribute('tabindex');
    }
  };

  const getGorgiasIframe = (modal) => {
    if (!modal) return null;
    const slot = modal.querySelector('[data-sd-gorgias-form-slot]');
    if (!slot) return null;
    return slot.querySelector('iframe[data-gorgias-iframe-contact-form]') || slot.querySelector('iframe');
  };

  const normalizeGorgiasEmbedSrc = (src) => {
    if (!src) return '';

    try {
      const url = new URL(src, window.location.href);
      url.searchParams.delete('_sd_reload');
      return url.toString();
    } catch (error) {
      return src;
    }
  };

  const preserveGorgiasIframeSrc = (iframe) => {
    if (!iframe) return '';

    const currentSrc = normalizeGorgiasEmbedSrc((iframe.getAttribute('src') || iframe.src || '').trim());
    if (currentSrc && currentSrc !== 'about:blank') {
      if (!iframe.dataset[ORIGINAL_SRC_ATTR]) {
        iframe.dataset[ORIGINAL_SRC_ATTR] = currentSrc;
      }
    }

    return iframe.dataset[ORIGINAL_SRC_ATTR] || '';
  };

  const reloadGorgiasIframe = (modal) => {
    const iframe = getGorgiasIframe(modal);
    if (!iframe) return false;

    const originalSrc = preserveGorgiasIframeSrc(iframe);
    if (!originalSrc) return false;

    try {
      const url = new URL(originalSrc, window.location.href);
      url.searchParams.set('_sd_reload', String(Date.now()));
      const reloadSrc = url.toString();
      iframe.setAttribute('src', reloadSrc);
      iframe.src = reloadSrc;
    } catch (error) {
      iframe.setAttribute('src', originalSrc);
      iframe.src = originalSrc;
    }

    return true;
  };

  const markGorgiasFormForReset = (modal) => {
    if (!modal) return;
    preserveGorgiasIframeSrc(getGorgiasIframe(modal));
    modal.dataset[FORM_RESET_ATTR] = '1';
  };

  const maybeResetGorgiasFormOnOpen = (modal) => {
    if (!modal || modal.dataset[FORM_RESET_ATTR] !== '1') return false;

    if (!reloadGorgiasIframe(modal)) return false;

    delete modal.dataset[FORM_RESET_ATTR];
    modal.classList.remove('sd-modal--success');
    return true;
  };

  const isMessageFromModalIframe = (event, modal) => {
    const iframe = getGorgiasIframe(modal);
    if (!iframe || !iframe.contentWindow) return true;

    if (event.source === iframe.contentWindow) return true;

    /* Nested Gorgias subframe — origin is already strict. */
    return true;
  };

  const enterModalSuccessMode = () => {
    const modal = getModal();
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.add('sd-modal--success');
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  };

  const openModal = (trigger) => {
    const modal = getModal();
    if (!modal) return false;

    if (trigger) lastModalOpener = trigger;

    maybeResetGorgiasFormOnOpen(modal);
    preserveGorgiasIframeSrc(getGorgiasIframe(modal));

    modal.classList.remove('sd-modal--success');
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sd-modal-open');
    modal.querySelector('[data-sd-close-modal], .sd-modal__close')?.focus?.();

    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return true;
  };

  const closeModal = ({ resetForm = false } = {}) => {
    const modal = getModal();
    if (!modal) return;

    releaseModalFocus(modal);
    modal.classList.remove('is-open', 'sd-modal--success');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sd-modal-open');

    if (resetForm) {
      markGorgiasFormForReset(modal);
    }
  };

  /*
   * The Student Gorgias form renders inside a cross-origin iframe
   * (contact.gorgias.help), so its script cannot read window.location of
   * this storefront page — and document.referrer is unreliable there
   * (origin-only under the browser's default strict-origin-when-cross-origin
   * policy). Read the real page locale here and answer the iframe's
   * request with it over postMessage.
   */
  const detectPageLocale = () => {
    try {
      const segments = (window.location.pathname || '/').split('/').filter(Boolean);
      if (!segments.length) return 'de';
      const first = String(segments[0] || '').toLowerCase();
      if (SUPPORTED_LOCALES.includes(first)) return first;
      const short = first.split('-')[0];
      if (SUPPORTED_LOCALES.includes(short)) return short;
    } catch (e) {
      /* ignore */
    }
    return 'de';
  };

  const postLocale = (targetWindow) => {
    if (!targetWindow) return;
    try {
      targetWindow.postMessage(
        { source: GORGIAS_MESSAGE_SOURCE, type: 'STUDENT_LOCALE', locale: detectPageLocale() },
        '*'
      );
    } catch (e) {
      /* Ignore cross-origin delivery failures. */
    }
  };

  const onGorgiasMessage = (event) => {
    if (event.origin !== GORGIAS_ORIGIN) return;

    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.source !== GORGIAS_MESSAGE_SOURCE) return;

    /* Answer directly to the requesting window — no modal/iframe lookup needed. */
    if (data.type === 'STUDENT_LOCALE_REQUEST') {
      postLocale(event.source);
      return;
    }

    const modal = getModal();
    if (!modal) return;

    if (!isMessageFromModalIframe(event, modal)) return;

    if (data.type === 'STUDENT_GORGIAS_SUCCESS') {
      enterModalSuccessMode();
      return;
    }

    if (data.type === 'STUDENT_GORGIAS_CLOSE') {
      closeModal({ resetForm: true });
    }
  };

  const initGorgiasBridge = () => {
    if (gorgiasBridgeReady) return;
    window.addEventListener('message', onGorgiasMessage);
    gorgiasBridgeReady = true;
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest(SELECTOR_OPEN)) {
      event.preventDefault();
      openModal(event.target.closest(SELECTOR_OPEN));
      return;
    }

    const modal = event.target.closest(SELECTOR_MODAL);
    if (!modal) return;

    if (event.target.closest('[data-sd-close-modal]')) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
  });

  initGorgiasBridge();
})();
